<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText } from "@chatfood/design-system";
import { computed, onBeforeMount } from "vue";
import { BasketViewCheckout } from "../basket-view-checkout";
import { BasketSummary } from "../basket-summary";
import { BasketClaims } from "../basket-claims";
import { useBasketStore } from "~/stores/basket";
import { useLocale } from "~/composables/use-locale";
import { useHeaderHeight } from "~/composables/use-header-height";

const css = bemBuilder("basket-view");
const { t } = useLocale();
const { headerHeightPixel } = useHeaderHeight();

const basket = useBasketStore();

const isEmpty = computed(() => {
  return basket.items.length === 0;
});

onBeforeMount(() => {
  if (!basket.isLoaded) {
    throw new Error("BasketView component requires basket store to be loaded.");
  }
});
</script>

<template>
  <div :class="[css(''), css('', 'self-scroll')]">
    <AtomText :class="css('title')" type="h2">
      {{ t("components.basket_view.title") }}
    </AtomText>
    <div v-if="isEmpty" :class="css('empty-basket')">
      <AtomText color="color-text-secondary">
        {{ t("components.basket_view.empty_message") }}
      </AtomText>
    </div>
    <template v-else>
      <div :class="css('content')">
        <div :class="css('block')">
          <BasketSummary />
        </div>
        <div :class="css('basket-claims')">
          <BasketClaims />
        </div>
      </div>
      <div :class="css('footer')">
        <BasketViewCheckout />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
$header-height: v-bind(headerHeightPixel);
$content-padding: calc(var(--spacing-2xl) * 2);

.basket-view {
  display: flex;
  flex-direction: column;

  &__title {
    padding: var(--spacing-lg);
  }

  &__block {
    padding: var(--spacing-lg);
    border-top: 1px solid var(--color-base-95);
  }

  &__basket-claims {
    padding: var(--spacing-lg);
    border-top: 1px solid var(--color-base-95);
  }

  &__footer {
    padding: var(--spacing-lg);
    border-top: 1px solid var(--color-base-95);
  }

  &__empty-basket {
    padding: var(--spacing-2xl) var(--spacing-md);
    text-align: center;
  }

  $content: "";

  &__content {
    $content: &;
  }

  @media (width >= $breakpoint-lg) {
    &--self-scroll {
      max-height: calc(100dvh - $header-height - $content-padding);
      overflow: auto;

      #{ $content } {
        flex-grow: 1;
        overflow-y: auto;
      }
    }
  }
}
</style>
